<script setup lang="ts">
import type { Product, Variant } from '~/types/product.type'
import Logo from '@/assets/logo.svg'
import { useModalStore } from '~/store/modalStore'

export interface RentingProduct {
  image: string
  title: string
  id: number
  price: number
  basePrice: number
  variant: Variant
}
const { data: products } = await useApi<Product[]>('/api/products/renting')
const { data: deposit } = await useApi<{ price: number }>('/api/products/deposit')
const { t } = useI18n()

const cartStore = useShoppingCartStore()
const modalStore = useModalStore()

const mappedRenting = computed(() => {
  if (!products.value)
    return []
  return products.value.reduce<RentingProduct[]>((acc, product) => {
    const mappedVariants = product.variants.map((variant) => {
      return {
        image: variant.images[0]?.url ?? null,
        title: variant.title,
        id: variant.id,
        price: variant.price,
        basePrice: variant.base_price,
        variant,
      }
    })
    return [...acc, ...mappedVariants]
  }, [])
})

const isLoading = ref(false)
const selectedVariants = computed({
  get: () => cartStore.shoppingCart.rent_items?.map((item: { variant: Variant }) => item?.variant),
  set: async (value) => {
    isLoading.value = true
    const rentItems = value.map((variant: Variant) => {
      return {
        variant,
      }
    })

    await cartStore.updateRentCart(rentItems)
    isLoading.value = false
  },
})

function showPostalCodesRentalModal() {
  modalStore.postalCodesRentalModalShown = true
}
</script>

<template>
  <div class="bg-background p-4 rounded-lg">
    <div class="flex flex-col lg:flex-row lg:gap-4 justify-between lg:items-center  mb-4">
      <h2 class="text-lg font-bold">
        {{ t('cart.boels_rental') }}
      </h2>

      <div class="flex flex-row gap-1 items-center text-sm">
        <span>
          {{ t('cart.only_available_with') }}
        </span>
        <button class="underline" @click="showPostalCodesRentalModal">
          {{ t('cart.certain_postal_code') }}
        </button>
      </div>
    </div>
    <div class="flex flex-col">
      <div v-for="(product, index) in mappedRenting" :key="product.id">
        <CartRentingProduct v-if="deposit?.price != null" v-model="selectedVariants" :renting-product="product" :is-loading="isLoading" :deposit="deposit.price" />
        <div v-if="index + 1 !== mappedRenting.length" class="h-px bg-primary/20 my-6" />
      </div>
    </div>
  </div>
</template>

<style>
[type='checkbox'].checkmark-gray:checked {
  background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='%23dc7818' stroke='DC7818' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e");
  @apply text-primary-light;
}
</style>
